/**
 * Endpoints of the merchant maintenance task list.
 *
 * The three approver roles share one route family and differ only by the
 * segment that names the role, so the paths are built from a small helper
 * rather than repeated per role.
 *
 * Only the endpoints already migrated to the shared HTTP layer are listed here.
 * The remaining actions still build their URL inline and will move in a later
 * sprint.
 */

/**
 * Route segment that identifies each role in the task list API.
 *
 * @readonly
 * @enum {string}
 */
export const ROLE = {
  INPUTER: 'inputer',
  APPROVER_1: 'approver-1',
  APPROVER_2: 'approver-2',
};

/**
 * Builds a task list endpoint for one role.
 *
 * @param {string} role - One of the {@link ROLE} values.
 * @param {string} action - Trailing route segment, such as `approved` or
 *   `send-back`. The approve and reject buttons supply this at call time.
 * @returns {string} Path relative to the base URL.
 */
export const taskListUrl = (role, action) =>
  `/index.php?r=api/v1/request_list/${role}/${action}`;

const api = {
  /**
   * Saves a revised submission. Both the merchant and the aggregator revision
   * screens post to this same route.
   */
  REVISION_UPDATE: taskListUrl(ROLE.INPUTER, 'update'),

  /**
   * Approves a batch of submissions in one request.
   *
   * @param {string} role - One of the {@link ROLE} values.
   * @returns {string} Path relative to the base URL.
   */
  bulkApprove: (role) => taskListUrl(role, 'approved-bulk'),

  /**
   * Approves or rejects one submission. The caller passes the decision as the
   * trailing segment, which is how the existing screens are wired.
   *
   * @param {string} role - One of the {@link ROLE} values.
   * @param {string} decision - Trailing segment naming the decision.
   * @returns {string} Path relative to the base URL.
   */
  decision: (role, decision) => taskListUrl(role, decision),

  /**
   * Sends a submission back to the previous step for revision.
   *
   * @param {string} role - One of the {@link ROLE} values.
   * @returns {string} Path relative to the base URL.
   */
  sendBack: (role) => taskListUrl(role, 'send-back'),
};

export default api;
